// src/app/core/store/policy-analytics/policy-analytics.reducer.ts

import { createReducer, on } from '@ngrx/store';
import {
  PolicyAnalyticsState,
  initialPolicyAnalyticsState,
  AsyncSlice,
  emptySlice,
} from './policy-analytics.state';
import {
  PaProductActions,
  PaConfigActions,
  PaLookupActions,
  PaOverviewActions,
  PaTimeSeriesActions,
  PaBreakdownActions,
  PaRankingsActions,
  PaComparisonActions,
} from './policy-analytics.actions';

// ── Helpers ───────────────────────────────────────────────────────────────────

function setLoading<T>(slice: AsyncSlice<T>): AsyncSlice<T> {
  return { ...slice, loading: true, error: null };
}

function setSuccess<T>(data: T): AsyncSlice<T> {
  return { data, loading: false, loaded: true, error: null };
}

function setFailure<T>(slice: AsyncSlice<T>, error: string): AsyncSlice<T> {
  return { ...slice, loading: false, error };
}

// ── Reducer ───────────────────────────────────────────────────────────────────

export const policyAnalyticsReducer = createReducer(
  initialPolicyAnalyticsState,

  // ── Products ──────────────────────────────────────────────────────────────
  on(PaProductActions.loadProducts, (state): PolicyAnalyticsState => ({
    ...state, productsLoading: true,
  })),
  on(PaProductActions.loadProductsSuccess, (state, { products }): PolicyAnalyticsState => ({
    ...state, products, productsLoading: false, productsLoaded: true,
  })),
  on(PaProductActions.loadProductsFailure, (state): PolicyAnalyticsState => ({
    ...state, productsLoading: false,
  })),
  on(PaProductActions.selectProduct, (state, { productTypeId }): PolicyAnalyticsState => ({
    ...state, selectedProductTypeId: productTypeId,
  })),

  // ── Config ────────────────────────────────────────────────────────────────
  on(PaConfigActions.loadConfig, (state): PolicyAnalyticsState => ({
    ...state, configLoading: true,
  })),
  on(PaConfigActions.loadConfigSuccess, (state, { productTypeId, config }): PolicyAnalyticsState => ({
    ...state,
    configLoading: false,
    configs: { ...state.configs, [productTypeId]: config },
  })),
  on(PaConfigActions.loadConfigFailure, (state): PolicyAnalyticsState => ({
    ...state, configLoading: false,
  })),

  // ── Lookups ───────────────────────────────────────────────────────────────
  on(PaLookupActions.loadCitiesSuccess, (state, { cities }): PolicyAnalyticsState => ({
    ...state, cities, citiesLoaded: true,
  })),
  on(PaLookupActions.loadGuaranteeSubtypesSuccess, (state, { subtypes }): PolicyAnalyticsState => ({
    ...state, guaranteeSubtypes: subtypes, guaranteeSubtypesLoaded: true,
  })),

  // ── Overview ────────────────────────────────────────────────────────────────
  on(PaOverviewActions.load, (state): PolicyAnalyticsState => ({
    ...state, overview: setLoading(state.overview),
  })),
  on(PaOverviewActions.success, (state, { data }): PolicyAnalyticsState => ({
    ...state, overview: setSuccess(data),
  })),
  on(PaOverviewActions.failure, (state, { error }): PolicyAnalyticsState => ({
    ...state, overview: setFailure(state.overview, error),
  })),
  on(PaOverviewActions.clear, (state): PolicyAnalyticsState => ({
    ...state, overview: emptySlice(),
  })),

  // ── TimeSeries ──────────────────────────────────────────────────────────────
  on(PaTimeSeriesActions.load, (state): PolicyAnalyticsState => ({
    ...state, timeSeries: setLoading(state.timeSeries),
  })),
  on(PaTimeSeriesActions.success, (state, { data }): PolicyAnalyticsState => ({
    ...state, timeSeries: setSuccess(data),
  })),
  on(PaTimeSeriesActions.failure, (state, { error }): PolicyAnalyticsState => ({
    ...state, timeSeries: setFailure(state.timeSeries, error),
  })),
  on(PaTimeSeriesActions.clear, (state): PolicyAnalyticsState => ({
    ...state, timeSeries: emptySlice(),
  })),

  // ── Breakdown ───────────────────────────────────────────────────────────────
  on(PaBreakdownActions.load, (state): PolicyAnalyticsState => ({
    ...state, breakdown: setLoading(state.breakdown),
  })),
  on(PaBreakdownActions.success, (state, { data }): PolicyAnalyticsState => ({
    ...state, breakdown: setSuccess(data),
  })),
  on(PaBreakdownActions.failure, (state, { error }): PolicyAnalyticsState => ({
    ...state, breakdown: setFailure(state.breakdown, error),
  })),
  on(PaBreakdownActions.clear, (state): PolicyAnalyticsState => ({
    ...state, breakdown: emptySlice(),
  })),

  // ── Rankings ────────────────────────────────────────────────────────────────
  on(PaRankingsActions.load, (state): PolicyAnalyticsState => ({
    ...state, rankings: setLoading(state.rankings),
  })),
  on(PaRankingsActions.success, (state, { data }): PolicyAnalyticsState => ({
    ...state, rankings: setSuccess(data),
  })),
  on(PaRankingsActions.failure, (state, { error }): PolicyAnalyticsState => ({
    ...state, rankings: setFailure(state.rankings, error),
  })),
  on(PaRankingsActions.clear, (state): PolicyAnalyticsState => ({
    ...state, rankings: emptySlice(),
  })),

  // ── Comparison ──────────────────────────────────────────────────────────────
  on(PaComparisonActions.load, (state): PolicyAnalyticsState => ({
    ...state, comparison: setLoading(state.comparison),
  })),
  on(PaComparisonActions.success, (state, { data }): PolicyAnalyticsState => ({
    ...state, comparison: setSuccess(data),
  })),
  on(PaComparisonActions.failure, (state, { error }): PolicyAnalyticsState => ({
    ...state, comparison: setFailure(state.comparison, error),
  })),
  on(PaComparisonActions.clear, (state): PolicyAnalyticsState => ({
    ...state, comparison: emptySlice(),
  })),
);
